import { PDFDocument } from 'pdf-lib';

/**
 * Recursively flatten nested formData into field/value pairs
 */
function flattenObject(obj, parentKey = '') {
  let flattened = [];
  for (let key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      flattened = flattened.concat(flattenObject(obj[key], `${parentKey}${key}.`));
    } else {
      const value = obj[key];
      flattened.push({ Field: `${parentKey}${key}`, Value: value });
    }
  }
  return flattened;
}

/**
 * Create a filled PDF from formData and return as Blob (frontend version)
 * @param {Object} formData - All form fields submitted from frontend
 * @param {string} investorType - "individual" or "entity"
 * @returns {Blob}
 */
export async function generatePDF(formData, investorType) {
  const templateUrl = '/template.pdf';
  const templateBytes = await fetch(templateUrl).then(res => res.arrayBuffer());

  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();

  const flat = flattenObject(formData);

  const allPdfFields = form.getFields().map(f => f.getName());

  flat.forEach(({ Field, Value }) => {
    if (["signature", "signatureEntityInd", "signatureEntity"].includes(Field)) return;
  
    if (!allPdfFields.includes(Field)) {
      console.warn(`⚠️ PDF does NOT contain field: "${Field}"`);
      return;
    }
  
    try {
      const field = form.getField(Field);
      console.log(`🧾 Setting: ${Field} = ${Value}`);
  
      if (typeof field.check === 'function') {
        const checked = Value === true || Value === 'true' || Value === 'on' || Value === 'checked';
        checked ? field.check() : field.uncheck();
      } else if (typeof field.select === 'function') {
        try {
          field.select(String(Value));
        } catch (e) {
          console.warn(`⚠️ Radio select failed for "${Field}":`, e);
        }
      } else if (typeof field.setText === 'function') {
        field.setText(String(Value));
      } else {
        console.warn(`⚠️ Field "${Field}" does not support check/select/setText`);
      }
    } catch (err) {
      console.warn(`⚠️ Could not set field "${Field}" with value "${Value}":`, err);
    }
  });  

  const pages = pdfDoc.getPages();

  // ✍️ Individual Signature
  if (formData.signature) {
    const pngImage = await pdfDoc.embedPng(formData.signature);
    const pngDims = pngImage.scale(0.25);

    if (pages.length >= 32) {
      pages[31].drawImage(pngImage, {
        x: 330,
        y: 430,
        width: pngDims.width,
        height: pngDims.height,
      });
    }

    if (pages.length >= 40) {
      pages[39].drawImage(pngImage, {
        x: 330,
        y: 585,
        width: pngDims.width,
        height: pngDims.height,
      });
    }
  }

  // ✍️ Individual (Entity) Signature
  if (formData.signatureEntityInd) {
    const pngImage = await pdfDoc.embedPng(formData.signatureEntityInd);
    const pngDims = pngImage.scale(0.25);

    if (pages.length >= 32) {
      pages[31].drawImage(pngImage, {
        x: 270,
        y: 280,
        width: pngDims.width,
        height: pngDims.height,
      });
    }

    if (pages.length >= 40) {
      pages[39].drawImage(pngImage, {
        x: 270,
        y: 400,
        width: pngDims.width,
        height: pngDims.height,
      });
    }
  }

  // ✍️ Individual Spouse Signature
  if (formData.signatureSpouse) {
    const pngImage = await pdfDoc.embedPng(formData.signatureSpouse);
    const pngDims = pngImage.scale(0.25);

    if (pages.length >= 32) {
      pages[31].drawImage(pngImage, {
        x: 370,
        y: 405,
        width: pngDims.width,
        height: pngDims.height,
      });
    }

    if (pages.length >= 40) {
      pages[39].drawImage(pngImage, {
        x: 370,
        y: 545,
        width: pngDims.width,
        height: pngDims.height,
      });
    }
  }

  // ✍️ Entity Signature
  if (formData.signatureEntity) {
    const pngImage = await pdfDoc.embedPng(formData.signatureEntity);
    const pngDims = pngImage.scale(0.25);

    if (pages.length >= 32) {
      pages[31].drawImage(pngImage, {
        x: 270,
        y: 280,
        width: pngDims.width,
        height: pngDims.height,
      });
    }

    if (pages.length >= 53) {
      pages[52].drawImage(pngImage, {
        x: 270,
        y: 580,
        width: pngDims.width,
        height: pngDims.height,
      });
    }
  }

  form.flatten();
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' }); // ✅ Blob for browser
}
